import { SlidersHorizontal } from "@phosphor-icons/react";
import { toFlattenedObject } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { debounce, get, isEqual, isNil } from "es-toolkit/compat";
import React, {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";
import Button from "../Button/Button";
import { ControlledDropdownInput } from "../DataInput/DropdownInput";
import { FilterDrawer } from "../FilterDrawer";
import { getFilterObject } from "../FilterDrawer/filterDrawerHelpers";
import Form from "../Form/Form";
import Pill from "../Pill/Pill";
import colours from "../constants/colours-vars.json";
import { useFilters } from "../utils/hooks/useFilters";
import useForm from "../utils/hooks/useForm";
import styles from "./FilterPanel.module.scss";
import ManageFiltersModal from "./ManageFiltersModal";
import { filterResponseToFilterConfig } from "./filterPanelHelpers";
import type { FilterPanelProps, FilterPanelValue } from "./types";

const cx = classNames.bind(styles);

/**
 * A standardized Panel to allow users to easily apply filters to a dataset.
 *
 * @deprecated this is not a valid shared React component. Use the new filtering patterns in the appropriate app you are building.
 */
const FilterPanel = ({
  filters = [],
  value,
  defaultValue = {},
  onChange,
  onReset,
  defaultActiveKeys,
  savedFilters,
  onSaveFilter,
  onEditFilter,
  onDeleteFilter,
  storageKey,
  className,
  ariaLabel,
  isVertical,
}: FilterPanelProps) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [showManageFiltersModal, setShowManageFiltersModal] = useState(false);
  const [storedFilters, setStoredFiltersState] =
    useFilters<FilterPanelValue>(storageKey);

  /**
   * It takes more than one re-render for storedFilters changes to reflect
   * after setSortedFilterState is called. But sometimes onRootFilterChange
   * function is called in the first render cycle, and that leads to an overwrite
   * of filter values. This ref is initially created with default values of the
   * filter panel, and gets updated whenever setSortedFilter is called.
   * And finally onRootFilterChange, uses this copy instead of actual storedFilters.
   * This is a workaround for when there's no value stored in session storage with storageKey.
   */
  const storedFiltersLocalCopy = useRef<any>(defaultValue ?? {});

  const setStoredFilters = useCallback(
    (newValue: any) => {
      storedFiltersLocalCopy.current = newValue;
      setStoredFiltersState(newValue);
    },
    [setStoredFiltersState]
  );

  const rootFilters = useMemo(
    () =>
      filters
        ?.filter(({ section }) => !section)
        .map(({ component }) => component),
    [filters]
  );

  const drawerFilters = useMemo(
    () => filters?.filter(({ section }) => !!section),
    [filters]
  );

  // If a defaultValue isn't provided, use the value from the initial render
  const defaultValues = useMemo(
    () => (Object.keys(defaultValue).length ? defaultValue : value),
    [defaultValue]
  );

  useDeepCompareEffectNoCheck(() => {
    /** Set filters to default on initial render */
    setStoredFilters(defaultValues ?? {});
    /** Restore the default filters on unmount - band-aid for FE-1967 */
    return () => setStoredFilters(defaultValues ?? {});
  }, [defaultValues, setStoredFilters]);

  const { watch, reset, setValue, ...restForm } = useForm({
    defaultValues,
    values: storedFilters,
  });

  const appliedFilterDrawerFiltersLength = useMemo(
    () =>
      drawerFilters.filter((filter) => {
        const currentValue = get(storedFilters, filter?.component?.props?.name);
        return (
          !isNil(currentValue) &&
          currentValue !== "" &&
          (!Array.isArray(currentValue) || currentValue.length)
        );
      }).length,
    [drawerFilters, storedFilters]
  );

  const onFilterChange = useCallback(
    debounce((newFormValues) => {
      setStoredFilters(newFormValues);
      if (onChange) {
        onChange(newFormValues);
      }
    }, 300),
    [onChange, setStoredFilters]
  );

  const savedFilterMatchesNewFilter = (values: FilterPanelValue) => {
    const {
      savedFilter,
      columnsToShow: _columnsToShow,
      ...filterValues
    } = values;
    const filter = savedFilters?.find(({ id }) => savedFilter === id)?.filter;

    if (!savedFilter || !filter) {
      return true;
    }

    return isEqual(
      getFilterObject(filterValues),
      savedFilters?.find(({ id }) => values.savedFilter === id)?.filter
    );
  };

  const onOpenFilterDrawer = () => setIsFilterDrawerOpen(true);
  const onCloseFilterDrawer = () => setIsFilterDrawerOpen(false);

  const onRootFilterChange = (value: unknown, filter: any) => {
    if (filter?.props?.onChange) {
      filter.props.onChange(value);
    }

    const newValue = {
      ...storedFiltersLocalCopy.current,
      [filter?.props?.name]: value,
    };
    if (savedFilterMatchesNewFilter(newValue)) {
      onFilterChange(newValue);
    } else {
      onFilterChange({
        ...newValue,
        savedFilter: undefined,
      });
    }
  };

  const onSaveFilterChange = (newValue: unknown) => {
    // Clear filters when the saved filter has been deselected
    if (!newValue) {
      // When clearing the filter, apply filter default values. Not an empty object.
      onFilterChange(defaultValues);
      return;
    }

    const newFilter = savedFilters?.find(({ id }) => newValue === id)?.filter;
    if (newFilter) {
      onFilterChange({
        ...filterResponseToFilterConfig(newFilter),
        savedFilter: newValue,
      });
    } else {
      const { savedFilter: _savedFilter, ...otherFilters } = value ?? {};

      onFilterChange(otherFilters);
    }
  };

  useEffect(() => {
    if (value && !Object.entries(value ?? {}).length) {
      reset({}, { keepDefaultValues: false });
    }
  }, [value]);

  const getCombinedFilterValues = (newValue: FilterPanelValue) => {
    if (!storageKey) {
      /** FE-1862: When filters are stored as search params, we need to flatten the internal representation */
      return Object.fromEntries(
        Object.entries({
          ...toFlattenedObject(storedFilters),
          ...toFlattenedObject(newValue),
        }).filter((value) => value !== undefined)
      );
    }
    return { ...storedFilters, ...newValue };
  };

  const onFilterDrawerChange = (newValue: FilterPanelValue) => {
    const combinedValues = getCombinedFilterValues(newValue);
    setStoredFilters(combinedValues);
    if (savedFilterMatchesNewFilter(combinedValues)) {
      onFilterChange(combinedValues);
    } else {
      onFilterChange({ ...combinedValues, savedFilter: undefined });
    }
  };

  const containerClassNameBuilder = useMemo(
    () =>
      [cx("container"), isVertical ? cx("container-vertical") : "", className]
        .filter((i) => !!i)
        .join(" "),
    [isVertical, className]
  );

  const rootFiltersContainerClassNameBuilder = useMemo(
    () =>
      [
        cx("rootFiltersContainer"),
        isVertical ? cx("rootFiltersContainer-vertical") : "",
      ]
        .filter((i) => !!i)
        .join(" "),
    [isVertical]
  );

  return (
    <div
      className={containerClassNameBuilder}
      role="region"
      aria-label={ariaLabel ?? "Filter Panel"}
    >
      <div className={rootFiltersContainerClassNameBuilder}>
        {/* IMPROVE: component type needs improvement */}
        <Form
          {...{ watch, reset, setValue, ...restForm }}
          isWrappedInFormContainer={false}
        >
          {rootFilters.map((filter) =>
            cloneElement(filter, {
              onChange: (newValue: unknown) =>
                onRootFilterChange(newValue, filter),
            })
          )}
          {savedFilters ? (
            <ControlledDropdownInput
              name="savedFilter"
              options={savedFilters}
              onChange={onSaveFilterChange}
              placeholder="Saved Filter"
              labelKey="name"
              valueKey="id"
              isSearchable
              isInline
              isOptionalTextShown={false}
            />
          ) : null}
        </Form>
      </div>

      {drawerFilters?.length ? (
        <>
          <Button onClick={onOpenFilterDrawer}>
            <div className={cx("filterButton")}>
              <SlidersHorizontal
                size={24}
                color={colours.text["400"]}
                weight="thin"
              />
              {appliedFilterDrawerFiltersLength ? (
                <Pill variant="info">{appliedFilterDrawerFiltersLength}</Pill>
              ) : null}
              Filters
            </div>
          </Button>

          <FilterDrawer
            isOpen={isFilterDrawerOpen}
            onClose={onCloseFilterDrawer}
            filters={drawerFilters}
            onChange={onFilterDrawerChange}
            onReset={() => {
              onReset?.(defaultValue);
              reset(defaultValue);
              setStoredFilters(defaultValue);
            }}
            value={storageKey ? storedFilters : (value ?? {})}
            defaultActiveKeys={defaultActiveKeys}
            onSaveFilter={onSaveFilter}
            onIconClick={
              savedFilters?.length && onEditFilter
                ? () => setShowManageFiltersModal(true)
                : undefined
            }
          />

          {savedFilters ? (
            <ManageFiltersModal
              isOpen={showManageFiltersModal}
              onClose={() => setShowManageFiltersModal(false)}
              onEditFilter={onEditFilter}
              onDeleteFilter={onDeleteFilter}
              savedFilters={savedFilters}
            />
          ) : undefined}
        </>
      ) : null}
    </div>
  );
};

export default FilterPanel;
